import { useAuthStore } from '@sportschool/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, School, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function SchoolSwitcher() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userContext = useAuthStore((s) => s.userContext);
  const activeSchool = userContext?.activeSchool;
  const mySchools = userContext?.mySchools || [];

  // Exclude switcher if the user only belongs to one school (or none loaded)
  if (mySchools.length <= 1 || !activeSchool) {
    return null;
  }

  const handleSchoolSwitch = async (schoolId: string) => {
    if (schoolId === activeSchool.id) return;

    // Update active school context in local storage
    localStorage.setItem('active_school_id', schoolId);

    // Invalidate TanStack Query caches to force a clean refetch of all school data
    await queryClient.clear();

    // Redirect to the new school's dashboard page
    navigate(`/schools/${schoolId}/dashboard`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2 px-3 border-muted-foreground/20 bg-background/50 hover:bg-muted text-foreground font-medium rounded-full shadow-sm hover:shadow transition-all duration-200"
        >
          <div className="flex size-5 items-center justify-center rounded-full bg-primary/10 text-primary">
            <School className="size-3" />
          </div>
          <span className="max-w-[120px] truncate text-xs font-semibold leading-none">
            {activeSchool.name}
          </span>
          <ChevronDown className="size-3.5 opacity-50 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-56 mt-1 rounded-xl shadow-lg border p-1 bg-popover text-popover-foreground animate-in fade-in-50 slide-in-from-top-1"
      >
        <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/75">
          Switch School
        </div>
        {mySchools.map((school) => {
          const isActive = school.id === activeSchool.id;
          return (
            <DropdownMenuItem
              key={school.id}
              onClick={() => handleSchoolSwitch(school.id)}
              className="flex items-center justify-between cursor-pointer px-2.5 py-2 rounded-lg text-sm transition-colors hover:bg-accent focus:bg-accent"
            >
              <div className="flex flex-col gap-0.5">
                <span className="font-semibold text-foreground leading-none">
                  {school.name}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {school.roles.join(', ')}
                </span>
              </div>
              {isActive && (
                <Check className="size-4 text-primary shrink-0 ml-2" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
